import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { Calendar, User, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // SEO meta data by language
  const seoData = {
    sk: {
      title: 'Blog',
      description: 'Tipy, návody a novinky zo sveta slnečných ochranných fólií. Ako vybrať fóliu, montáž svojpomocne, výhody fólií na okná.',
      keywords: 'blog fólie, tipy fólie, návody montáž fólií, výhody slnečných fólií'
    },
    cs: {
      title: 'Blog',
      description: 'Tipy, návody a novinky ze světa slunečních ochranných fólií. Jak vybrat fólii, montáž svépomocí, výhody fólií na okna.',
      keywords: 'blog fólie, tipy fólie, návody montáž fólií'
    },
    en: {
      title: 'Blog',
      description: 'Tips, guides and news from the world of sun protection films. How to choose film, DIY installation, benefits of window films.',
      keywords: 'window film blog, film tips, installation guides, film benefits'
    },
    de: {
      title: 'Blog',
      description: 'Tipps, Anleitungen und Neuigkeiten aus der Welt der Sonnenschutzfolien. Wie wählt man Folie, Selbstmontage, Vorteile.',
      keywords: 'Folien Blog, Folien Tipps, Montageanleitungen, Folien Vorteile'
    },
    ru: {
      title: 'Блог',
      description: 'Советы, инструкции и новости из мира солнцезащитных пленок. Как выбрать пленку, монтаж своими руками, преимущества.',
      keywords: 'блог пленки, советы пленки, инструкции по монтажу, преимущества пленок'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.sk;

  const categories = [
    { id: 'all', label: t('blog.all_posts') },
    { id: 'tips', label: t('blog.category_tips') },
    { id: 'guides', label: t('blog.category_guides') },
    { id: 'news', label: t('blog.category_news') }
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const url = selectedCategory === 'all' 
          ? `${API_URL}/api/blog`
          : `${API_URL}/api/blog?category=${selectedCategory}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setPosts(data);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [selectedCategory]);

  const getTranslation = (post) => {
    const lang = getUiLang(i18n);
    return post.translations[lang] || post.translations['sk'] || post.translations['en'];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        keywords={currentSeo.keywords}
        url="/blog"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="blog-title">
            {t('blog.title')}
          </h1>
          <p className="text-xl opacity-90">{t('blog.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-full transition ${
                selectedCategory === cat.id
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-orange-100'
              }`}
              data-testid={`blog-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {t('blog.no_posts')}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map(post => {
              const translation = getTranslation(post);
              return (
                <article
                  key={post.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition group"
                  data-testid="blog-post-card"
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.images[0] || 'https://via.placeholder.com/400x225'}
                        alt={translation.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {post.author}
                        </span>
                      </div>
                      <h2 className="text-xl font-bold mb-2 group-hover:text-orange-500 transition">
                        {translation.title}
                      </h2>
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {translation.excerpt}
                      </p>
                      <span className="inline-flex items-center text-orange-500 font-medium">
                        {t('blog.read_more')}
                        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition" />
                      </span>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
